import { useEffect, useRef } from 'react';
import { contactPhone } from '@/mocks/homeData';

export default function InteriorSection() {
  const sectionRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            entry.target.classList.add('animate-fade-in-up');
            entry.target.classList.remove('opacity-0');
            observer.unobserve(entry.target);
          }
        });
      },
      { threshold: 0.15 }
    );

    const elements = sectionRef.current?.querySelectorAll('.reveal');
    elements?.forEach((el) => observer.observe(el));

    return () => observer.disconnect();
  }, []);

  return (
    <section id="interior" ref={sectionRef} className="relative w-full py-20 md:py-32 bg-background-100">
      <div className="w-full px-4 md:px-8 lg:px-12 max-w-6xl mx-auto">
        {/* 섹션 타이틀 */}
        <div className="text-center mb-12 md:mb-16">
          <span className="reveal opacity-0 inline-block px-4 py-1.5 mb-6 text-sm font-medium tracking-widest text-foreground-600 border border-foreground-300 rounded-full uppercase">
            Interior Design
          </span>
          <h2 className="reveal opacity-0 font-heading text-4xl md:text-5xl font-semibold text-foreground-900 mb-4 text-balance">
            프리미엄 인테리어
          </h2>
          <p className="reveal opacity-0 text-foreground-600 text-lg md:text-xl max-w-2xl mx-auto">
            쾌적한 공간 구성과 트렌디한 인테리어로 일상의 품격을 높입니다
          </p>
        </div>

        {/* 상단 대형 이미지 + 배지 */}
        <div className="reveal opacity-0 relative mb-8 md:mb-12 rounded-2xl overflow-hidden border border-background-200/70">
          <div className="aspect-[16/9] overflow-hidden">
            <img
              src="https://static.readdy.ai/image/4e318e9e38f95d8b588b811d6b898be4/2d42f3f079f9a744d76dad99ffac84cd.png"
              alt="프리미엄 침실 인테리어"
              className="w-full h-full object-cover object-top"
              width={1400}
              height={700}
            />
          </div>
          {/* 좌측 상단 배지 그룹 */}
          <div className="absolute top-4 left-4 md:top-6 md:left-6 flex flex-col gap-2">
            <span className="inline-block px-4 py-2 text-sm md:text-base font-bold rounded-lg bg-primary-600 text-background-50">
              쾌적한 공간 구성
            </span>
            <span className="inline-block px-4 py-2 text-sm md:text-base font-bold rounded-lg bg-accent-600 text-background-50">
              트렌디한 인테리어
            </span>
          </div>
        </div>

        {/* 중앙 카피 + 해시태그 */}
        <div className="reveal opacity-0 text-center mb-10 md:mb-14">
          <h3 className="font-heading text-3xl md:text-4xl font-bold text-foreground-900 mb-3 leading-snug">
            특별 공급분 확정분양가 적용
            <br className="hidden sm:block" />
            <span className="text-primary-600">10년 민간임대아파트</span>
          </h3>
          <div className="flex flex-wrap justify-center gap-2 md:gap-3 mt-5">
            {['#특별 공급분', '#확정분양가 적용', '#임대가로 내 집 마련'].map((tag) => (
              <span
                key={tag}
                className="px-4 py-1.5 text-sm md:text-base font-medium rounded-full bg-secondary-100 text-secondary-800 border border-secondary-200"
              >
                {tag}
              </span>
            ))}
          </div>
        </div>

        {/* 하단 이미지 그리드 */}
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 md:gap-5">
          <div className="reveal opacity-0 rounded-2xl overflow-hidden border border-background-200/70 aspect-[4/3]">
            <img
              src="https://public.readdy.ai/ai/img_res/edited_daa5d21c0822832aeafe2054455f4de5_33720ea4.jpg"
              alt="욕실 인테리어"
              className="w-full h-full object-cover object-top transition-transform duration-700 hover:scale-105"
              width={600}
              height={450}
            />
          </div>
          <div className="reveal opacity-0 rounded-2xl overflow-hidden border border-background-200/70 aspect-[4/3]">
            <img
              src="https://static.readdy.ai/image/4e318e9e38f95d8b588b811d6b898be4/85a114ad23f4b086f63ba6f1066d6b46.png"
              alt="거실 및 다이닝 인테리어"
              className="w-full h-full object-cover object-top transition-transform duration-700 hover:scale-105"
              width={600}
              height={450}
            />
          </div>
        </div>

        {/* 하단 CTA */}
        <div className="reveal opacity-0 flex justify-center mt-10 md:mt-14">
          <a
            href={`tel:${contactPhone}`}
            className="inline-flex items-center gap-2 px-8 py-4 rounded-xl bg-primary-500 text-background-50 text-lg md:text-xl font-semibold whitespace-nowrap transition-all hover:bg-primary-600 hover:scale-[1.02]"
          >
            <i className="ri-phone-line text-xl" />
            전화상담 바로받기
          </a>
        </div>
      </div>
    </section>
  );
}